<template>
  <div class="flex flex-col">
    <div
      class="group/field bg-surface-container-low rounded-large relative mt-2 cursor-text overflow-hidden border-[3px] border-transparent"
      :class="[
        error &&
          'border-error hover:border-on-error-container text-on-surface focus-within:border-error!',
        !error &&
          'border-outline hover:border-on-surface text-on-surface-variant hover:text-on-surface focus-within:border-primary!',
      ]"
      @click="focusDraftInput"
    >
      <div class="max-h-48 overflow-y-auto">
        <div
          v-if="isDraftFocused || hasContent"
          class="bg-surface-container-low sticky top-0 z-10"
          :class="emailEntries.length > 0 ? 'h-6' : 'h-4'"
          aria-hidden="true"
        />
        <div
          class="flex flex-wrap items-center gap-1 px-4"
          :class="
            emailEntries.length > 0 ? 'pb-2' : isDraftFocused || hasContent ? 'pb-4' : 'min-h-14'
          "
        >
          <BaseChip
            v-for="(entry, index) in emailEntries"
            :key="`${entry.email}-${index}`"
            v-tooltip="entry.error"
            :icon="!entry.error ? 'person' : 'error'"
            type="input"
            class="group relative max-w-full overflow-hidden"
            :class="[
              entry.error &&
                'border-error! bg-error-container text-on-error-container! [&_.material-symbols-outlined]:text-on-error-container!',
            ]"
            @click.stop="removeEmail(index)"
          >
            <span class="truncate">{{ entry.email }}</span>
            <BaseStateLayer />
          </BaseChip>
          <input
            ref="draftInput"
            v-model="emailDraft"
            type="text"
            class="text-body-large min-w-32 grow bg-transparent outline-none placeholder:text-transparent"
            :class="[
              error && 'text-on-surface',
              !error && 'text-on-surface-variant hover:text-on-surface focus:text-on-surface',
            ]"
            :placeholder="label"
            @keydown="onDraftKeydown"
            @input="onDraftInput"
            @blur="onDraftBlur"
            @focus="isDraftFocused = true"
            @paste="onDraftPaste"
          />
        </div>
      </div>
      <label
        class="pointer-events-none absolute left-3 z-10 bg-inherit px-1 transition-all"
        :class="[
          isDraftFocused || hasContent
            ? 'text-body-small top-1.5 translate-y-0'
            : 'text-body-large top-1/2 -translate-y-1/2',
          error && isDraftFocused && 'text-error',
          error && !isDraftFocused && 'text-error group-hover/field:text-on-error-container',
          !error && isDraftFocused && 'text-primary',
          !error && !isDraftFocused && 'text-on-surface-variant group-hover/field:text-on-surface',
        ]"
      >
        {{ label }}{{ required ? '*' : '' }}
      </label>
    </div>
    <p v-if="error" class="text-error text-body-small mt-1 px-4">
      {{ $t(error, { field: label }) }}
    </p>
    <p
      v-else-if="numberOfValidEmails > 0"
      class="text-on-surface-variant text-body-small mt-1 px-4"
    >
      {{ $t('common.emails', { count: numberOfValidEmails }) }}
    </p>
    <p v-else-if="supportingText" class="text-on-surface-variant text-body-small mt-1 px-4">
      {{ supportingText }}
    </p>
  </div>
</template>
<script lang="ts" setup>
  import { computed, ref, useTemplateRef } from 'vue'
  import BaseChip from '../chip/BaseChip.vue'
  import BaseStateLayer from '../state-layer/BaseStateLayer.vue'
  import { useI18n } from 'vue-i18n'

  // Unicode letters/digits + common mailbox chars; rejects <>!# and similar.
  // Allows diacritics (e.g. müller@example.com) from spreadsheet pastes.
  const EMAIL_REGEX =
    /^[\p{L}\p{N}]+(?:[._%+-][\p{L}\p{N}]+)*@[\p{L}\p{N}]+(?:[.-][\p{L}\p{N}]+)*\.[\p{L}]{2,}$/u
  const DELIMITER_REGEX = /[,;\s]+/

  interface EmailEntry {
    email: string
    error: string | undefined
  }

  interface Props {
    label: string
    error?: string
    required?: boolean
    excludedEmails?: ReadonlyArray<string>
    supportingText?: string
  }

  const props = defineProps<Props>()

  const emails = defineModel<string[]>({ required: true })
  const emailCandidates = ref<string[]>([])

  const emailDraft = ref('')
  const isDraftFocused = ref(false)
  const draftInput = useTemplateRef<HTMLInputElement>('draftInput')

  const excludedEmailSet = computed(() => {
    return new Set((props.excludedEmails ?? []).map((email) => email.toLocaleLowerCase()))
  })

  const { t } = useI18n()

  const isEmailValid = (email: string) => {
    if (!EMAIL_REGEX.test(email)) {
      return t('email.invalid')
    }
    if (excludedEmailSet.value.has(email.toLocaleLowerCase())) {
      return t('email.alreadyExists')
    }
  }

  const emailEntries = computed<EmailEntry[]>(() => {
    return emailCandidates.value.map((email) => ({
      email,
      error: isEmailValid(email),
    }))
  })

  const numberOfValidEmails = computed(() => {
    return emailEntries.value.filter((entry) => !entry.error).length
  })

  const hasContent = computed(() => emailCandidates.value.length > 0 || emailDraft.value.length > 0)

  const addEmails = (emailsToAdd: ReadonlyArray<string>) => {
    for (const email of emailsToAdd) {
      const trimmed = email.trim()
      if (!trimmed) continue

      const alreadyAdded = emailCandidates.value.some(
        (existing) => existing.toLocaleLowerCase() === trimmed.toLocaleLowerCase(),
      )
      if (alreadyAdded) continue

      emailCandidates.value.push(trimmed)
    }

    // defineModel reads from the parent prop, so assigning in a loop would
    // overwrite previous adds before the parent re-renders. Batch into one write.
    emails.value = emailEntries.value.filter((entry) => !entry.error).map((entry) => entry.email)
  }

  const removeEmail = (index: number) => {
    emailCandidates.value = emailCandidates.value.filter((_, emailIndex) => emailIndex !== index)
    emails.value = emailEntries.value.filter((entry) => !entry.error).map((entry) => entry.email)
  }

  const commitDraftParts = () => {
    if (!DELIMITER_REGEX.test(emailDraft.value)) return

    const endsWithDelimiter = /[,;\s]$/.test(emailDraft.value)
    const parts = emailDraft.value.split(DELIMITER_REGEX)
    const emailsToCommit = endsWithDelimiter
      ? parts.filter((part) => part.length > 0)
      : parts.slice(0, -1).filter((part) => part.length > 0)
    const remainder = endsWithDelimiter ? '' : (parts[parts.length - 1] ?? '')

    addEmails(emailsToCommit)
    emailDraft.value = remainder
  }

  const commitDraft = () => {
    const trimmed = emailDraft.value.trim()
    if (!trimmed) {
      emailDraft.value = ''
      return
    }
    addEmails([trimmed])
    emailDraft.value = ''
  }

  const onDraftInput = () => {
    commitDraftParts()
  }

  const onDraftKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitDraft()
      return
    }

    if (
      event.key === 'Backspace' &&
      emailDraft.value.length === 0 &&
      emailCandidates.value.length > 0
    ) {
      event.preventDefault()
      removeEmail(emailCandidates.value.length - 1)
    }
  }

  const onDraftBlur = () => {
    isDraftFocused.value = false
    commitDraft()
  }

  const onDraftPaste = (event: ClipboardEvent) => {
    const pastedText = event.clipboardData?.getData('text')
    if (!pastedText || !DELIMITER_REGEX.test(pastedText)) return

    // Spreadsheet pastes usually have no trailing delimiter, so commit every
    // non-empty part instead of leaving the last address in the draft.
    event.preventDefault()
    const parts = `${emailDraft.value}${pastedText}`
      .split(DELIMITER_REGEX)
      .map((part) => part.trim())
      .filter((part) => part.length > 0)

    addEmails(parts)
    emailDraft.value = ''
  }

  const focusDraftInput = () => {
    draftInput.value?.focus()
  }
</script>
